import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/mangas';

const navGroups: { label: string; to: string }[] = [
  { label: '首页', to: '/' },
  ...categories.map((c) => ({ label: c.name, to: `/category/${c.slug}` })),
  { label: '新番连载', to: '/category/new' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchTerm.trim();
    navigate(q ? `/category/all?q=${encodeURIComponent(q)}` : '/category/all');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/90 backdrop-blur-md shadow-[0_1px_0_0_oklch(var(--background-200))]'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] max-w-7xl items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="樱花漫画网 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-flower-fill text-lg"></i>
          </span>
          <span
            className={`font-heading text-2xl font-bold ${
              solid ? 'text-primary-600' : 'text-background-50'
            }`}
          >
            樱花漫画
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {navGroups.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-background-50'
                    : solid
                      ? 'text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                      : 'text-background-100 hover:bg-background-50/20 hover:text-background-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Search + CTA */}
        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden md:flex items-center gap-2 rounded-full px-3 py-2 ${
              solid ? 'bg-background-100' : 'bg-background-50/20'
            }`}
            role="search"
          >
            <i
              className={`ri-search-line text-sm ${solid ? 'text-foreground-500' : 'text-background-100'}`}
            ></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索漫画…"
              className={`w-28 lg:w-36 bg-transparent text-sm outline-none placeholder:text-sm ${
                solid ? 'text-foreground-900 placeholder:text-foreground-400' : 'text-background-50 placeholder:text-background-200'
              }`}
            />
          </form>
          <Link
            to="/category/new"
            className="hidden md:inline-flex items-center gap-1 rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition hover:bg-accent-600"
          >
            <i className="ri-flashlight-fill"></i>
            去追更
          </Link>

          {/* Hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="展开菜单"
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition lg:hidden ${
              solid ? 'text-foreground-800 hover:bg-background-200' : 'text-background-50 hover:bg-background-50/20'
            }`}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2 rounded-full bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索漫画…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {navGroups.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-medium whitespace-nowrap ${
                    isActive ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}